import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button, Chip,
    CircularProgress, Alert, Breadcrumbs, Link, Tabs, Tab,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Avatar, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
    List, ListItem, ListItemText, ListItemButton, IconButton
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;
const getJwt = () => localStorage.getItem("feathers-jwt");

function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

export const GruppiDetailPage = () => {
    const { gruppoId } = useParams();
    const navigate = useNavigate();
    const notify = useNotify();

    const [gruppo, setGruppo] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [tabValue, setTabValue] = React.useState(0);

    // Dati per i tab
    const [users, setUsers] = React.useState([]);
    const [loadingUsers, setLoadingUsers] = React.useState(false);
    const [piani, setPiani] = React.useState([]);
    const [loadingPiani, setLoadingPiani] = React.useState(false);
    const [availablePiani, setAvailablePiani] = React.useState([]);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [loadingDialog, setLoadingDialog] = React.useState(false);

    const loadGruppoDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();

            const response = await axios.get(`${host}/tbl-gruppo/${gruppoId}`, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });

            setGruppo(response.data);
        } catch (error) {
            console.error("Errore caricamento gruppo:", error);
            setError("Errore nel caricamento del gruppo");
            notify("Errore nel caricamento del gruppo", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const loadUsers = async () => {
        try {
            setLoadingUsers(true);
            const jwt = getJwt();

            // Carica associazioni tbl_users_gruppo
            const response = await axios.get(`${host}/tbl-users-gruppo`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    tbl_gruppoId: parseInt(gruppoId),
                    $sort: { ordinamento: 1 }
                }
            });

            const associations = response.data.data || [];

            // Carica dettagli users
            if (associations.length > 0) {
                const userIds = associations.map(a => a.usersId);
                const usersResponse = await axios.get(`${host}/users`, {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        id: { $in: userIds },
                        $limit: 100
                    }
                });

                const usersData = usersResponse.data.data || [];
                
                // Merge associazioni con dati users
                const enrichedUsers = associations.map(assoc => {
                    const user = usersData.find(u => u.id === assoc.usersId);
                    return {
                        ...assoc,
                        userName: user?.name || 'N/A',
                        userEmail: user?.email || 'N/A',
                        userRole: user?.roles || 'user'
                    };
                });

                setUsers(enrichedUsers);
            } else {
                setUsers([]);
            }
        } catch (error) {
            console.error("Errore caricamento users:", error);
            notify("Errore nel caricamento utenti", { type: 'error' });
        } finally {
            setLoadingUsers(false);
        }
    };

    const loadPiani = async () => {
        try {
            setLoadingPiani(true);
            const jwt = getJwt();

            const response = await axios.get(`${host}/tbl-piani`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    gruppoId: parseInt(gruppoId),
                    $sort: { ordinamento: 1, nome: 1 }
                }
            });

            setPiani(response.data.data || []);
        } catch (error) {
            console.error("Errore caricamento piani:", error);
            notify("Errore nel caricamento piani", { type: 'error' });
        } finally {
            setLoadingPiani(false);
        }
    };

    const loadAvailablePiani = async () => {
        try {
            setLoadingDialog(true);
            const jwt = getJwt();

            // Carica tutti i piani non ancora collegati a questo gruppo
            const response = await axios.get(`${host}/tbl-piani`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    $or: [
                        { gruppoId: null },
                        { gruppoId: { $ne: parseInt(gruppoId) } }
                    ],
                    flAttivo: 1,
                    flAnnullato: 0,
                    $sort: { nome: 1 },
                    $limit: 100
                }
            });

            setAvailablePiani(response.data.data || []);
        } catch (error) {
            console.error("Errore caricamento piani disponibili:", error);
            notify("Errore nel caricamento piani disponibili", { type: 'error' });
        } finally {
            setLoadingDialog(false);
        }
    };

    const handleAddPiano = async (pianoId) => {
        try {
            const jwt = getJwt();
            
            await axios.patch(`${host}/tbl-piani/${pianoId}`, {
                gruppoId: parseInt(gruppoId)
            }, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });

            notify('Piano aggiunto al gruppo', { type: 'success' });
            setDialogOpen(false);
            loadPiani();
        } catch (error) {
            console.error("Errore aggiunta piano:", error);
            notify("Errore nell'aggiunta del piano", { type: 'error' });
        }
    };

    const handleRemovePiano = async (pianoId, pianoNome) => {
        if (!window.confirm(`Vuoi rimuovere il piano "${pianoNome}" da questo gruppo?`)) {
            return;
        }

        try {
            const jwt = getJwt();
            
            await axios.patch(`${host}/tbl-piani/${pianoId}`, {
                gruppoId: null
            }, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });

            notify('Piano rimosso dal gruppo', { type: 'success' });
            loadPiani();
        } catch (error) {
            console.error("Errore rimozione piano:", error);
            notify("Errore nella rimozione del piano", { type: 'error' });
        }
    };

    const handleOpenDialog = () => {
        setDialogOpen(true);
        loadAvailablePiani();
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        
        // Lazy loading dei dati
        if (newValue === 1 && users.length === 0 && !loadingUsers) {
            loadUsers();
        } else if (newValue === 2 && piani.length === 0 && !loadingPiani) {
            loadPiani();
        }
    };

    React.useEffect(() => {
        loadGruppoDetails();
    }, [gruppoId]);

    if (loading) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ ml: 2 }}>Caricamento dettagli gruppo...</Typography>
                </Box>
            </Box>
        );
    }

    if (error || !gruppo) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <Alert severity="error">{error || "Gruppo non trovato"}</Alert>
                <Button variant="outlined" onClick={() => navigate('/gruppi')} sx={{ mt: 2 }}>
                    Torna ai Gruppi
                </Button>
            </Box>
        );
    }

    const getStatusColor = () => {
        if (gruppo.flAnnullato) return { color: '#f44336', bgcolor: '#ffebee', label: 'Annullato' };
        if (!gruppo.flAttivo) return { color: '#ff9800', bgcolor: '#fff3e0', label: 'Inattivo' };
        if (!gruppo.flVisibile) return { color: '#9e9e9e', bgcolor: '#f5f5f5', label: 'Nascosto' };
        return { color: '#4caf50', bgcolor: '#e8f5e8', label: 'Attivo' };
    };

    const status = getStatusColor();

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>

                <Breadcrumbs sx={{ mb: 3 }}>
                    <Link component="button" variant="body2" onClick={() => navigate('/gruppi')} sx={{ textDecoration: 'none' }}>
                        Gruppi
                    </Link>
                    <Typography variant="body2" color="text.primary">{gruppo.nome}</Typography>
                </Breadcrumbs>

                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            <GroupsIcon sx={{ fontSize: 32, verticalAlign: 'middle', mr: 1, color: '#1976d2' }} />
                            {gruppo.nome}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip label={status.label} size="small" sx={{ color: status.color, bgcolor: status.bgcolor, fontWeight: 500 }} />
                            <Chip 
                                icon={gruppo.flVisibile ? <VisibilityIcon /> : <VisibilityOffIcon />} 
                                label={gruppo.flVisibile ? 'Visibile' : 'Nascosto'} 
                                size="small" 
                                variant="outlined" 
                            />
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/gruppi')}>
                            Indietro
                        </Button>
                        <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/gruppi/${gruppoId}/edit`)}>
                            Modifica
                        </Button>
                    </Box>
                </Box>

                <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                    <Tabs 
                        value={tabValue} 
                        onChange={handleTabChange}
                        sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                    >
                        <Tab icon={<InfoIcon />} label="Informazioni" iconPosition="start" />
                        <Tab icon={<PersonIcon />} label="Utenti Collegati" iconPosition="start" />
                        <Tab icon={<CategoryIcon />} label="Piani Collegati" iconPosition="start" />
                    </Tabs>

                    {/* Tab 0: Informazioni Generali */}
                    <TabPanel value={tabValue} index={0}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="overline" color="text.secondary">Nome Gruppo</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{gruppo.nome}</Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="overline" color="text.secondary">Ordinamento</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{gruppo.ordinamento || 0}</Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={12}>
                                <Card variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="overline" color="text.secondary">Note</Typography>
                                    <Typography variant="body1" sx={{ mt: 1 }}>
                                        {gruppo.note || <em style={{ color: '#9e9e9e' }}>Nessuna nota disponibile</em>}
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="overline" color="text.secondary">Visibile</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        {gruppo.flVisibile ? (
                                            <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50' }} />
                                        ) : (
                                            <CancelIcon sx={{ fontSize: 40, color: '#f44336' }} />
                                        )}
                                    </Box>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="overline" color="text.secondary">Attivo</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        {gruppo.flAttivo ? (
                                            <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50' }} />
                                        ) : (
                                            <CancelIcon sx={{ fontSize: 40, color: '#f44336' }} />
                                        )}
                                    </Box>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="overline" color="text.secondary">Annullato</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        {gruppo.flAnnullato ? (
                                            <CancelIcon sx={{ fontSize: 40, color: '#f44336' }} />
                                        ) : (
                                            <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50' }} />
                                        )}
                                    </Box>
                                </Card>
                            </Grid>
                            {gruppo.createdAt && (
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="overline" color="text.secondary">Data Creazione</Typography>
                                        <Typography variant="body1" sx={{ mt: 1 }}>
                                            {new Date(gruppo.createdAt).toLocaleString('it-IT')}
                                        </Typography>
                                    </Card>
                                </Grid>
                            )}
                            {gruppo.updatedAt && (
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="overline" color="text.secondary">Ultimo Aggiornamento</Typography>
                                        <Typography variant="body1" sx={{ mt: 1 }}>
                                            {new Date(gruppo.updatedAt).toLocaleString('it-IT')}
                                        </Typography>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>
                    </TabPanel>

                    {/* Tab 1: Utenti Collegati */}
                    <TabPanel value={tabValue} index={1}>
                        {loadingUsers ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : users.length > 0 ? (
                            <>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                    {users.length} {users.length === 1 ? 'Utente collegato' : 'Utenti collegati'}
                                </Typography>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Utente</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Ruolo</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 600 }}>Ordinamento</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 600 }}>Stato</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {users.map((userAssoc, index) => (
                                                <TableRow key={userAssoc.id} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2' }}>
                                                                {userAssoc.userName.charAt(0).toUpperCase()}
                                                            </Avatar>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {userAssoc.userName}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>{userAssoc.userEmail}</TableCell>
                                                    <TableCell>
                                                        <Chip label={userAssoc.userRole} size="small" sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }} />
                                                    </TableCell>
                                                    <TableCell align="center">{userAssoc.ordinamento || 0}</TableCell>
                                                    <TableCell align="center">
                                                        {userAssoc.flAttivo && !userAssoc.flAnnullato ? (
                                                            <Chip label="Attivo" size="small" sx={{ bgcolor: '#e8f5e8', color: '#4caf50' }} />
                                                        ) : (
                                                            <Chip label="Inattivo" size="small" sx={{ bgcolor: '#fff3e0', color: '#ff9800' }} />
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        ) : (
                            <Alert severity="info">Nessun utente collegato a questo gruppo</Alert>
                        )}
                    </TabPanel>

                    {/* Tab 2: Piani Collegati */}
                    <TabPanel value={tabValue} index={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {loadingPiani ? 'Caricamento...' : `${piani.length} ${piani.length === 1 ? 'Piano collegato' : 'Piani collegati'}`}
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleOpenDialog}
                                disabled={loadingPiani}
                            >
                                Aggiungi Piano
                            </Button>
                        </Box>

                        {loadingPiani ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : piani.length > 0 ? (
                            <>
                                <Grid container spacing={2}>
                                    {piani.map((piano) => {
                                        let pianoStatus = 'Attivo', pianoColor = '#4caf50', pianoBgcolor = '#e8f5e8';
                                        if (piano.flAnnullato) { pianoStatus = 'Annullato'; pianoColor = '#f44336'; pianoBgcolor = '#ffebee'; }
                                        else if (!piano.flAttivo) { pianoStatus = 'Inattivo'; pianoColor = '#ff9800'; pianoBgcolor = '#fff3e0'; }

                                        return (
                                            <Grid item xs={12} md={6} key={piano.id}>
                                                <Card variant="outlined" sx={{ p: 2, '&:hover': { boxShadow: 2 }, position: 'relative' }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemovePiano(piano.id, piano.nome);
                                                        }}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 8,
                                                            color: '#f44336',
                                                            '&:hover': { bgcolor: '#ffebee' }
                                                        }}
                                                    >
                                                        <RemoveCircleIcon fontSize="small" />
                                                    </IconButton>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1, pr: 4, cursor: 'pointer' }}
                                                        onClick={() => navigate(`/piani/${piano.id}/edit`)}>
                                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{piano.nome}</Typography>
                                                        <Chip label={pianoStatus} size="small" sx={{ color: pianoColor, bgcolor: pianoBgcolor }} />
                                                    </Box>
                                                    <Box sx={{ cursor: 'pointer' }} onClick={() => navigate(`/piani/${piano.id}/edit`)}>
                                                        {piano.DescrizioneCompleta && (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                                {piano.DescrizioneCompleta.length > 100 ? `${piano.DescrizioneCompleta.substring(0, 100)}...` : piano.DescrizioneCompleta}
                                                            </Typography>
                                                        )}
                                                        <Divider sx={{ my: 1 }} />
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={6}>
                                                                <Typography variant="caption" color="text.secondary">Prezzo</Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>€ {piano.Prezzo || '0.00'}</Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography variant="caption" color="text.secondary">Max Squadre</Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{piano.maxNumeroSquadre || '-'}</Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </>
                        ) : (
                            <Alert severity="info">Nessun piano collegato a questo gruppo</Alert>
                        )}
                    </TabPanel>
                </Card>

                {/* Dialog per aggiungere piani */}
                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">Aggiungi Piano al Gruppo</Typography>
                            <IconButton size="small" onClick={() => setDialogOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent dividers>
                        {loadingDialog ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : availablePiani.length > 0 ? (
                            <List>
                                {availablePiani.map((piano) => (
                                    <ListItem key={piano.id} disablePadding>
                                        <ListItemButton onClick={() => handleAddPiano(piano.id)}>
                                            <ListItemText
                                                primary={piano.nome}
                                                secondary={
                                                    <>
                                                        {piano.DescrizioneCompleta && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {piano.DescrizioneCompleta.length > 60 ? `${piano.DescrizioneCompleta.substring(0, 60)}...` : piano.DescrizioneCompleta}
                                                            </Typography>
                                                        )}
                                                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                            <Chip label={`€ ${piano.Prezzo || '0.00'}`} size="small" />
                                                            {piano.maxNumeroSquadre && (
                                                                <Chip label={`Max ${piano.maxNumeroSquadre} squadre`} size="small" variant="outlined" />
                                                            )}
                                                        </Box>
                                                    </>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Alert severity="info">Nessun piano disponibile da aggiungere</Alert>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>Chiudi</Button>
                    </DialogActions>
                </Dialog>

            </motion.div>
        </Box>
    );
};
